<script setup lang="ts">
import { onMounted, ref, shallowRef } from 'vue';
import { useRoute } from 'vue-router';
import Routes from '@/constants/Routes';
import { t } from '@/i18n';
import {
  DxForm,
  DxRequiredRule,
  DxSimpleItem,
  DxCompareRule,
  DxGroupItem,
  DxButtonOptions,
  DxButtonItem
} from 'devextreme-vue/form';
import Icons from '@/constants/Icons';
import { useAlertStore } from '@/services/stores/alertStore';
import SubmitButtonContent from '@/components/devextreme/SubmitButtonContent.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import { useRouter } from 'vue-router';
import type { IPasswordResetRequest } from '@/types/interfaces/LoginTypes';
import { useLoginService } from '@/services/api/loginService';

definePage({
  name: 'LoginPasswordReset',
  meta: {
    anonymous: true
  }
});

const login = useLoginService();
const router = useRouter();
const loading = ref<boolean>(false);
const valid = ref<boolean>(true);
const passwordFormRef = shallowRef<DxForm>();
const route = useRoute();
const alert = useAlertStore();

const passwordDetails = ref<IPasswordResetRequest>({
  emailAddress: route.query.email as string || '',
  resetToken: route.query.resetToken as string || '',
  password: '',
  passwordConfirmation: ''
});

async function onSubmit(): Promise<void> {
  loading.value = true;
  const response = await login.resetPassword(passwordDetails.value);
  if (response.success) {
    await router.push({ name: Routes.Login.Root });
    alert.showSuccess(t('login.passwordReset'));
    return;
  }

  loading.value = false;
  alert.showError(response.problem?.detail ?? t('login.resetPasswordFailed'));
  passwordFormRef.value?.instance?.reset();
}

onMounted(() => {
  const errors: Array<string> = [];

  if (passwordDetails.value.emailAddress === '') {
    errors.push(t('login.emailMissing'));
  }

  if (passwordDetails.value.resetToken === '') {
    errors.push(t('login.resetTokenMissing'));
  }

  alert.showWarning(null, errors);
  valid.value = errors.length === 0;
});
</script>

<template>
  <form @submit.prevent="onSubmit">
    <DxForm :disabled="loading" :form-data="passwordDetails" label-mode="floating" :show-validation-summary="true"
        ref="passwordFormRef">
      <DxSimpleItem data-field="password" :editor-options="{ mode: 'password' }">
        <DxRequiredRule :message="$t('validation.required', { field: $t('fields.password') })" />
      </DxSimpleItem>
      <DxSimpleItem data-field="passwordConfirmation" :editor-options="{ mode: 'password' }">
        <DxRequiredRule :message="$t('validation.required', { field: $t('fields.passwordConfirmation') })" />
        <DxCompareRule :message="$t('validation.doesNotMatch', { field: $t('fields.passwordConfirmation') })" :ignore-empty-value="true"
            :comparison-target="() => passwordDetails.password" />
      </DxSimpleItem>

      <DxGroupItem :col-count="2">
        <DxButtonItem>
          <DxButtonOptions template="sendButtonTemplate" type="primary" :use-submit-behavior="true" width="100%"
              :disabled="!valid" />
        </DxButtonItem>
        <DxButtonItem>
          <DxButtonOptions template="cancelButtonTemplate" type="secondary" width="100%" />
        </DxButtonItem>
      </DxGroupItem>

      <template #sendButtonTemplate>
        <SubmitButtonContent :loading="loading" :icon="Icons.Key" :text="$t('login.updatePassword')" />
      </template>

      <template #cancelButtonTemplate>
        <ButtonContent :text="$t('common.cancel')" :icon="Icons.Ban" :to="{ name: Routes.Login.Root }" />
      </template>
    </DxForm>
  </form>
</template>

<style scoped>

</style>